import { Component, Input, Output, EventEmitter, OnInit, OnChanges } from '@angular/core';

type ConflictLayout = 'unidade' | 'area' | 'combinacao' | 'misto' | 'geolocation';

export interface ConflictItem {
  id: string | number;
  type: 'area' | 'unidade' | 'combinacao' | 'geolocation';
  companyName?: string;
  areaName?: string;
  configurationName?: string;
  cutOffDate?: string | Date | null;
  employeeName?: string;
  _originalEmployeeId?: any;
}

export interface ConflictResolutionResult {
  resolved: boolean;
  resolutions: { [key: string]: string };
  cancelled: boolean;
}

@Component({
  selector: 'app-conflict-resolution-modal',
  templateUrl: './conflict-resolution-modal.component.html',
  styleUrls: ['./conflict-resolution-modal.component.scss']
})
export class ConflictResolutionModalComponent implements OnInit, OnChanges {
  
  @Input() showModal = false;
  @Input() conflicts: ConflictItem[] = [];
  @Input() title = 'Existe um impedimento a ser resolvido';
  aceiteImpedimento = false;
  @Input() message = '';
  @Input() cutOffDate?: string | Date | null;
  @Input() enableBulkActions = false;
  
  @Output() modalResult = new EventEmitter<ConflictResolutionResult>();
  @Output() modalClosed = new EventEmitter<void>();

  resolutions: { [key: string]: string } = {};
  conflictLayout: ConflictLayout = 'unidade';

  keepExistingRuleAll = false;
  applyNewRuleAll = false;

  ngOnInit(): void {
    this.initializeResolutions();
    this.updateConflictLayout();
  }

  ngOnChanges(): void {
    if (this.conflicts.length > 0) {
      this.initializeResolutions();
      this.updateConflictLayout();
    }
  }

  private initializeResolutions(): void {
    this.aceiteImpedimento = false;
    this.resolutions = {};
    this.conflicts.forEach(conflict => {
      this.resolutions[conflict.id.toString()] = '';
    });
    this.keepExistingRuleAll = false;
    this.applyNewRuleAll = false;
  }

  private updateConflictLayout(): void {
    if (!this.conflicts || this.conflicts.length === 0) {
      this.conflictLayout = 'unidade';
      return;
    }

    const types = new Set(this.conflicts.map(conflict => conflict.type));

    if (types.has('geolocation')) {
      this.conflictLayout = 'geolocation';
      return;
    }

    if (types.has('combinacao')) {
      this.conflictLayout = 'combinacao';
      return;
    }

    if (types.size === 1) {
      this.conflictLayout = types.has('area') ? 'area' : 'unidade';
      return;
    }

    this.conflictLayout = 'misto';
  }

  isAllResolutionsSelected(): boolean {
    if (this.conflicts.length === 0) {
      return false;
    }

    return this.conflicts.every(conflict => {
      const resolution = this.resolutions[conflict.id.toString()];
      return resolution === 'current' || resolution === 'conflicting';
    });
  }

  onSalvar(): void {
    if (!this.isAllResolutionsSelected()) {
      return;
    }

    const result: ConflictResolutionResult = {
      resolved: true,
      resolutions: this.buildResolutions(),
      cancelled: false
    };
    
    this.modalResult.emit(result);
    this.closeModal();
  }

  onCancel(): void {
    const result: ConflictResolutionResult = {
      resolved: false,
      resolutions: {},
      cancelled: true
    };
    
    this.modalResult.emit(result);
    this.closeModal();
  }

  closeModal(): void {
    this.showModal = false;
    this.modalClosed.emit();
  }

  getConflictTypeLabel(type: ConflictItem['type']): string {
    if (type === 'area') return 'Área';
    if (type === 'combinacao') return 'Unidade e Área';
    if (type === 'geolocation') return 'Geolocalização';
    return 'Unidade';
  }

  trackByConflictId(index: number, conflict: ConflictItem): string | number {
    return conflict.id;
  }

  getAlertMessage(): string {
    if (this.message) {
      return this.message;
    }

    if (this.conflictLayout === 'area') {
      return 'Uma ou mais áreas selecionadas já estão inseridas em outra configuração.';
    }

    if (this.conflictLayout === 'combinacao') {
      return 'Uma ou mais combinações de unidade e área já está inserida em outra configuração.';
    }

    if (this.conflictLayout === 'geolocation') {
      return 'Um ou mais colaboradores já possuem regras de geolocalização cadastradas';
    }

    // Default/unidade ou misto cai aqui
    return 'Uma ou mais unidades selecionadas já está inserida em outra configuração.';
  }

  getAcknowledgementText(): string {
    if (this.conflictLayout === 'geolocation') {
      return 'Estou ciente que ao selecionar ‘aplicar nova regra’, as configurações anteriores para esse colaborador serão substituídas.';
    }

    const scope =
      this.conflictLayout === 'area'
        ? 'a área acima'
        : this.conflictLayout === 'combinacao'
          ? 'a combinação de unidade e área acima'
          : 'a unidade acima';

    const cutOffDate = this.getCutOffDateText();
    const cutOffSuffix = cutOffDate
      ? ` ao final da data de corte da configuração anterior (${cutOffDate}).`
      : '.';

    return `Estou ciente que ao salvar, ${scope} será removida da configuração anterior, porém só passará a obedecer as novas regras configuradas aqui${cutOffSuffix}`;
  }

  getConfigurationName(conflict: ConflictItem): string {
    return conflict.configurationName || 'Configuração atual';
  }

  getCompanyName(conflict: ConflictItem): string {
    return conflict.companyName || '-';
  }

  getAreaName(conflict: ConflictItem): string {
    return conflict.areaName || '-';
  }
  
  getEmployeeName(conflict: ConflictItem): string {
    return conflict.employeeName || '-';
  }

  private getCutOffDateText(): string | null {
    const cutOffDate = this.cutOffDate || this.conflicts.find(conflict => !!conflict.cutOffDate)?.cutOffDate;

    if (!cutOffDate) {
      return null;
    }

    if (typeof cutOffDate === 'string') {
      const parsed = new Date(cutOffDate);
      if (!isNaN(parsed.getTime())) {
        return parsed.toLocaleDateString('pt-BR');
      }
      return cutOffDate;
    }

    return cutOffDate.toLocaleDateString('pt-BR');
  }

  onResolutionChange(conflictId: string, resolution: string): void {
    this.resolutions[conflictId] = resolution;
  }

  private buildResolutions(): { [key: string]: string } {
    return { ...this.resolutions };
  }

  createSampleConflicts(): ConflictItem[] {
    return [
      {
        id: 1,
        type: 'area',
        areaName: 'Área de Desenvolvimento',
        configurationName: 'Config. Padrão',
        cutOffDate: '2025-10-01'
      },
      {
        id: 2,
        type: 'unidade',
        companyName: 'Unidade Matriz',
        configurationName: 'Config. Padrão',
        cutOffDate: '2025-10-01'
      },
      {
        id: 3,
        type: 'combinacao',
        companyName: 'Filial São Paulo',
        areaName: 'Tecnologia',
        configurationName: 'Config. Padrão',
        cutOffDate: '2025-10-01'
      }
    ];
  }

  get isBulkActionActive(): boolean {
    return this.keepExistingRuleAll || this.applyNewRuleAll;
  }

  onToggleKeepExistingAll(checked: boolean): void {
    this.keepExistingRuleAll = checked;
    if (checked) {
      this.applyNewRuleAll = false;
      this.setAllResolutions('current');
    }
  }

  onToggleApplyNewAll(checked: boolean): void {
    this.applyNewRuleAll = checked;
    if (checked) {
      this.keepExistingRuleAll = false;
      this.setAllResolutions('conflicting');
    }
  }

  private setAllResolutions(resolution: string): void {
    this.conflicts.forEach(conflict => {
      this.resolutions[conflict.id.toString()] = resolution;
    });
  }
}
